import React, { useState } from 'react';

import type { RJSFSchema } from '@rjsf/utils';

import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';

import RoomDraftForm from './RoomDraftForm';
import type { FlashOfferRoomDraft } from './utils';

type RoomsStepProperty = Pick<App.Property, 'id' | 'name'> & {
  category?: string | null;
  channel_manager?: string | null;
};

interface Props {
  amenityOptions: Array<string>;
  hasRoomDraftFormError: boolean;
  isCreatingRooms: boolean;
  isLoadingRoomDraftForm: boolean;
  isLoadingRoomTypesSchema: boolean;
  onAddAmenityOption: (amenityName: string) => void;
  onAddRoomDraft: () => void;
  onRemoveRoomDraft: (draftId: string) => void;
  onRetryLoadRoomFields: () => void;
  onUpdateRoomDraft: (draftId: string, updates: Partial<FlashOfferRoomDraft>) => void;
  property: RoomsStepProperty | null;
  roomDrafts: Array<FlashOfferRoomDraft>;
  roomTypesFormSchema?: RJSFSchema;
}

export default function RoomsStep({
  amenityOptions,
  hasRoomDraftFormError,
  isCreatingRooms,
  isLoadingRoomDraftForm,
  isLoadingRoomTypesSchema,
  onAddAmenityOption,
  onAddRoomDraft,
  onRemoveRoomDraft,
  onRetryLoadRoomFields,
  onUpdateRoomDraft,
  property,
  roomDrafts,
  roomTypesFormSchema,
}: Props) {
  const [roomDraftDeleteId, setRoomDraftDeleteId] = useState<string | null>(null);

  return (
    <>
      <Stack spacing={2}>
        {!property?.id && <Alert severity="error">Select or create a property before creating rooms.</Alert>}

        {property?.id && (
          <>
            {roomDrafts.length === 0 && (
              <Alert severity="warning">
                No Salesforce room details were returned for this offer. Add rooms manually or continue without creating
                rooms.
              </Alert>
            )}

            {isLoadingRoomDraftForm && (
              <Alert icon={<CircularProgress size={16} />} severity="info">
                Loading room fields.
              </Alert>
            )}

            {hasRoomDraftFormError && (
              <Alert
                severity="error"
                action={
                  <Button
                    onClick={() => {
                      onRetryLoadRoomFields();
                    }}
                  >
                    Try again
                  </Button>
                }
              >
                Unable to load room fields.
              </Alert>
            )}

            {roomDrafts.map((roomDraft, index) => {
              const isExistingRoom = !!roomDraft.existingRoomName;
              const isSelectedForCreate = roomDraft.shouldCreate && !isExistingRoom;
              const isRoomInputDisabled = isCreatingRooms || !isSelectedForCreate;

              return (
                <Accordion variant="outlined" key={roomDraft.draftId} defaultExpanded disableGutters>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      minHeight: 72,
                      '&.Mui-expanded': { minHeight: 72 },
                      '& .MuiAccordionSummary-content': { my: 1.5 },
                      '& .MuiAccordionSummary-content.Mui-expanded': { my: 1.5 },
                    }}
                  >
                    <Box flexGrow={1}>
                      <Typography variant="h6">
                        {roomDraft.name || roomDraft.existingRoomName || `Room ${index + 1}`}
                      </Typography>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Typography variant="body2" color="text.secondary">
                          {roomDraft.category}
                        </Typography>
                      </Stack>
                    </Box>

                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      onClick={(event) => event.stopPropagation()}
                      onFocus={(event) => event.stopPropagation()}
                      sx={{ ml: 2 }}
                    >
                      <IconButton
                        aria-label={`Remove room ${index + 1}`}
                        disabled={isCreatingRooms}
                        onClick={() => setRoomDraftDeleteId(roomDraft.draftId)}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </Stack>
                  </AccordionSummary>

                  <AccordionDetails>
                    <Stack spacing={2}>
                      {roomTypesFormSchema ? (
                        <RoomDraftForm
                          amenityOptions={amenityOptions}
                          channelManager={property.channel_manager}
                          disabled={isRoomInputDisabled}
                          onAddAmenityOption={onAddAmenityOption}
                          onChange={(updates) => onUpdateRoomDraft(roomDraft.draftId, updates)}
                          propertyCategory={property.category}
                          propertyName={property.name}
                          roomDraft={roomDraft}
                          schema={roomTypesFormSchema}
                        />
                      ) : (
                        <Alert severity="info">Room fields will appear when the schema has loaded.</Alert>
                      )}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              );
            })}

            <Box>
              <Button
                variant="text"
                startIcon={<AddIcon />}
                onClick={onAddRoomDraft}
                disabled={isCreatingRooms || isLoadingRoomTypesSchema}
              >
                Add room
              </Button>
            </Box>
          </>
        )}
      </Stack>

      <Dialog open={!!roomDraftDeleteId} onClose={() => setRoomDraftDeleteId(null)}>
        <DialogTitle>Remove room draft?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This removes the draft from this setup flow. It does not delete an existing room.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoomDraftDeleteId(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              if (roomDraftDeleteId) {
                onRemoveRoomDraft(roomDraftDeleteId);
                setRoomDraftDeleteId(null);
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
